import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { TableWidget } from "../table-widget/TableWidget";
import './CveDetailsPage.scss';
interface CveData {
  cveId: string;
  description: string;
  cvssVersion: string;
  nistBaseScore: number;
  cisaBaseScore: number;
  deviceIds: number[];
}

export const CveDetailsPage = () => {
  const { operatorId, cveId } = useParams();
  const [cveData, setCveData] = useState<CveData | null>(null);
  const [cveDataDevices, setCveDataDevices] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Memoized function to fetch CVE data
  const fetchCveData = useCallback(async () => {
    if (!operatorId || !cveId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `/v2/op/device-cves/nvd-cve-threats/${operatorId}/${cveId}`
      );
      setCveData(response.data.cveThreats);
      
      // Also set device IDs from the API response
      if (response.data.cveThreats?.deviceIds) {
        setCveDataDevices(response.data.cveThreats.deviceIds);
      }
    } catch (err) {
      console.error("Error fetching CVE details:", err);
      setError("Failed to load CVE details. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [operatorId, cveId]);

  // Fetch device IDs from localStorage
  const fetchCveDataDevices = useCallback(() => {
    try {
      const storedCveData = localStorage.getItem("cveData");
      if (storedCveData) {
        const parsed = JSON.parse(storedCveData);
        const deviceIds = parsed.map((id: string | number) => Number(id));
        setCveDataDevices(deviceIds);
      }
    } catch (error) {
      console.error("Error parsing CVE devices from localStorage:", error);
    }
  }, []);

  // Single useEffect for data fetching
  useEffect(() => {
    fetchCveData();
    fetchCveDataDevices();
  }, [fetchCveData, fetchCveDataDevices]);

  const backHome = () => {
    navigate(`/operators/${operatorId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading CVE details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-red-500 text-lg mb-4">{error}</div>
        <button 
          onClick={fetchCveData}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!cveData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">No CVE data found.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mx-auto p-6">
      <header className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex flex-col items-start">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {cveData.cveId} Details
          </h1>
          <p className="text-md text-gray-600">
            Details of CVEs affecting impacted devices.
          </p>
        </div>
      </header>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{cveData.cveId}</h2>
          <button 
            onClick={backHome}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>

        <div className="mb-6">
          <h3 className="font-semibold text-lg mb-2">Description:</h3>
          <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
            {cveData.description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-1">CVSS Version:</h4>
            <span className="text-gray-900">{cveData.cvssVersion}</span>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-1">NIST Base Score:</h4>
             <div className={`inline-block  text-white px-3 py-1 rounded text-sm ${cveData.nistBaseScore}`}>{cveData.nistBaseScore}</div>
            <span  className="inline-block  text-white px-3 py-1 rounded text-sm">
              {cveData.nistBaseScore}
            </span>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-1">CISA-ADP Base Score:</h4>
            <span className={`inline-block  text-white px-3 py-1 rounded text-sm ${cveData.nistBaseScore}`}>
              {cveData.cisaBaseScore}
            </span>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="font-semibold text-lg mb-2">NVD Reference:</h4>
          <a
            href={`https://nvd.nist.gov/vuln/detail/${cveData.cveId}`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 underline transition-colors"
          >
            View {cveData.cveId} on NVD website
          </a>
        </div>

        <div className="mb-6">      
          <div className="rounded-lg shadow-sm">
            <TableWidget
              hasSearch={true}
              url={`v2/op/device/list-devices-by-deviceIds`}
              countPerPage={8}
              title="Affected Devices"
              searchPositionRight={true}
              deviceId={cveDataDevices}
              isDns={true}
              needCategories={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};